import { ReactNode, useState } from 'react';
import { Button } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import clsx from 'clsx';
import { useReviewOffer } from 'components/ReviewOffer/ReviewOffer.context';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { useNumberFormat } from 'hooks/useNumberFormat';
import { shouldShowNoAvailableFunds } from 'utils/application/application.helpers';
import { useFormattedOfferDuration } from 'utils/durationHelper';
import { deriveProductFromTypeAndDuration } from 'utils/productHelper';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import {
  isRequestedAmount,
  Offer,
  TColumnData,
  TField,
} from '../SummaryCard.helpers';
import styles from '../SummaryCard.module.css';
import { WithdrawalFee, TWithdrawalFeeOfferData } from '../WithdrawalFee';
import { ColumnHeader } from './ColumnHeader/ColumnHeader';
import { FirstMonthlyPayment } from './FirstMonthlyPayment/FirstMonthlyPayment';
import {
  InitialCommission,
  TInitialCommission,
} from './InitialCommission/InitialCommission';
import { MonthlyPayments } from './MonthlyPayments/MonthlyPayments';
import { RCFWithdrawalFee } from './RCFWithdrawalFee/RCFWithdrawalFee';
import { RepeatCommission } from './RepeatCommission/RepeatCommission';
import {
  getClipboardText,
  getOfferSelectionStatus,
  hasRequiredWithdrawalFeeData,
} from './SummaryCardColumn.helpers';
import { Value } from './Value/Value';

type TProps = {
  applicationId: string;
  hasAction: boolean;
  fields: TField[];
  offer: TColumnData;
};

export const SummaryCardColumn = ({
  applicationId,
  hasAction,
  offer,
  fields,
}: TProps) => {
  const DEFAULT_WITHDRAWAL_FEE_RATE = 0.05;
  const { isUK } = useLocale();
  const { trackEvent } = useAmplitude();
  const { formatCurrency, formatPercentage } = useNumberFormat();
  const formattedDuration = useFormattedOfferDuration(
    offer.duration,
    offer.interest_only_repayments,
  );
  const [initialCommission, setInitialCommission] =
    useState<TInitialCommission>(null);

  const { name: productName, type: derivedProductType } =
    deriveProductFromTypeAndDuration({
      productType: offer.product_type,
      duration: offer.duration,
      isUK: isUK,
    });

  const {
    selectedOfferId,
    offerDecision,
    hasMultipleOffers,
    selectedApplication,
    setSelectedOfferId,
    setOfferDecision,
  } = useReviewOffer();

  const [isLoadingPayments, setIsLoadingPayments] = useState(false);

  const [withdrawalFee, setWithdrawalFee] = useState(
    derivedProductType === 'term_loan' && !offer.adjustable_fee
      ? 0.06
      : offer.withdrawal_fee_percentage === undefined
        ? DEFAULT_WITHDRAWAL_FEE_RATE
        : offer.withdrawal_fee_percentage,
  );

  const [formattedMonthlyRepayments, setFormattedMonthlyRepayments] = useState<
    string | null
  >(null);
  const [formattedRCFWithdrawalFee, setFormattedRCFWithdrawalFee] = useState<
    string | null
  >(null);
  const [formattedFirstMonthlyPayment, setFormattedFirstMonthlyPayment] =
    useState<string | null>(null);

  const isPartnerChannel = selectedApplication.is_partner_channel;

  const isSingleOffer = !hasMultipleOffers;
  const {
    offer_id: offerId,
    max_amount,
    total_drawdown_amount,
    available_funds,
  } = offer;
  const interestRate = offer.interest_rates?.[0]?.rate;

  const hasDrawnDown = available_funds !== max_amount;

  const handleConfirmClick = () => {
    if (!offerId) return;

    if (selectedOfferId === offerId || isSingleOffer) {
      trackEvent(AMPLITUDE_EVENTS.acceptOfferClicked, {
        'Product type': derivedProductType,
        'CTA text': 'Accept this offer',
        'Number of offers':
          selectedApplication.formal_offer_sets[0].formal_offers.length,
      });

      setOfferDecision(Offer.AGREE);
    } else {
      trackEvent(AMPLITUDE_EVENTS.seeDetailsClicked, {
        'Product type': derivedProductType,
        'CTA text': 'See details',
      });

      setOfferDecision(Offer.UNDECIDED);
    }
    setSelectedOfferId(offerId);
  };

  const selection = getOfferSelectionStatus(
    offerDecision,
    selectedOfferId,
    offerId,
  );

  const { checkIntroducerPermission } = useIntroducerData();
  const { offerIsSelected, offerNotSelected } = selection;
  const hasSeenDetails = offerIsSelected || isSingleOffer;

  const clipboardText = getClipboardText({
    isUK,
    offer,
    withdrawalFee,
    formattedMonthlyRepayments,
    formattedRCFWithdrawalFee,
    formattedFirstMonthlyPayment,
    formatCurrency,
    formatPercentage,
    initialCommission,
    isRequestedAmount: isRequestedAmount(selectedApplication),
    fields,
  });

  const valuesMap: { [key in TField]?: ReactNode } = {
    max_amount: (
      <Value
        key="max_amount"
        value={formatCurrency(max_amount)}
        offerSelection={selection}
      />
    ),
    available_funds: hasDrawnDown ? (
      <Value
        key="available_funds"
        value={formatCurrency(available_funds)}
        offerSelection={selection}
      />
    ) : null,
    interest_rates: (
      <Value
        key="interest_rates"
        value={formatPercentage(interestRate)}
        offerSelection={selection}
      />
    ),
    withdrawal_fee_percentage: (
      <Value key="withdrawal_fee_percentage" offerSelection={selection}>
        {hasRequiredWithdrawalFeeData(offer) ? (
          <WithdrawalFee
            isDisabled={!hasAction}
            offer={offer as TWithdrawalFeeOfferData}
            applicationId={applicationId}
            isLoadingPayments={isLoadingPayments}
            setIsLoadingPayments={setIsLoadingPayments}
            showExpectedCommissions={
              selectedApplication.show_expected_commissions
            }
            withdrawalFee={withdrawalFee}
            offerIsSelected={offerIsSelected}
            offerNotSelected={offerNotSelected}
            productName={productName}
            setWithdrawalFee={setWithdrawalFee}
            isPartnerChannel={isPartnerChannel}
          />
        ) : null}
      </Value>
    ),
    total_drawdown_amount: (
      <Value
        key="total_drawdown_amount"
        value={formatCurrency(total_drawdown_amount)}
        className={styles.withdrawValue}
        offerSelection={selection}
      />
    ),
    initial_commission: (
      <Value key="initial_commission" offerSelection={selection}>
        {offer.offer_id ? (
          <InitialCommission
            setInitialCommission={setInitialCommission}
            applicationId={applicationId}
            offerId={offer.offer_id}
            isLoadingPayments={isLoadingPayments}
            offerIsSelected={offerIsSelected}
            offerNotSelected={offerNotSelected}
          />
        ) : (
          '-'
        )}
      </Value>
    ),
    repeat_commission: (
      <Value key="repeat_commission" offerSelection={selection}>
        {offer.offer_id ? (
          <RepeatCommission
            applicationId={applicationId}
            offerId={offer.offer_id}
            isLoadingPayments={isLoadingPayments}
            offerIsSelected={offerIsSelected}
            offerNotSelected={offerNotSelected}
          />
        ) : (
          '-'
        )}
      </Value>
    ),
    monthly_repayments: (
      <Value
        key="monthly_repayments"
        offerSelection={selection}
        className={styles.monthlyPayments}
      >
        {offer.offer_id ? (
          <MonthlyPayments
            applicationId={applicationId}
            isLoadingPayments={isLoadingPayments}
            offer={offer}
            withdrawalFee={withdrawalFee}
            setWithdrawalFee={setWithdrawalFee}
            setFormattedMonthlyRepayments={setFormattedMonthlyRepayments}
          />
        ) : null}
      </Value>
    ),
    rcf_withdrawal_fee: (
      <Value key="rcf_withdrawal_fee" offerSelection={selection}>
        {offer.offer_id && offer.max_amount ? (
          <RCFWithdrawalFee
            offerId={offer.offer_id}
            maxAmount={offer.max_amount}
            offerIsSelected={offerIsSelected}
            offerNotSelected={offerNotSelected}
            setFormattedRCFWithdrawalFee={setFormattedRCFWithdrawalFee}
          />
        ) : null}
      </Value>
    ),
    first_monthly_payment: (
      <Value key="first_monthly_payment" offerSelection={selection}>
        {offer.offer_id && offer.max_amount ? (
          <FirstMonthlyPayment
            offerId={offer.offer_id}
            maxAmount={offer.max_amount}
            offerIsSelected={offerIsSelected}
            offerNotSelected={offerNotSelected}
            setFormattedFirstMonthlyPayment={setFormattedFirstMonthlyPayment}
          />
        ) : null}
      </Value>
    ),
  };

  return (
    <div
      data-testid="product-column"
      className={clsx(styles.column, styles.values)}
    >
      <ColumnHeader
        applicationId={applicationId}
        offer={offer}
        hasAction={hasAction}
        clipboardText={clipboardText}
      />

      {fields.map((field) => valuesMap[field])}

      <div className={styles.buttonContainer}>
        {!isPartnerChannel &&
          !shouldShowNoAvailableFunds(selectedApplication) &&
          offerDecision === Offer.UNDECIDED &&
          hasAction && (
            <Button
              data-testid={`select-${formattedDuration
                .toLowerCase()
                .split(' ')
                .join('-')}`}
              variant={hasSeenDetails ? 'primary' : 'secondary'}
              disabled={
                hasSeenDetails &&
                !checkIntroducerPermission('can_interact_with_offers')
              }
              className={styles.button}
              onClick={handleConfirmClick}
            >
              {hasSeenDetails ? t`Accept this offer` : t`See details`}
            </Button>
          )}
      </div>
    </div>
  );
};
